/**
 * Invented causation: "inflated valuations caused by thin evidence packs".
 *
 * ITC Tax run 4 shipped a LinkedIn lead that joined two lines of the source
 * with a causal connector the source never used: "(thin evidence packs; missed
 * deadlines)" and "challenging inflated valuations" are two facts on two lines,
 * and "We challenge inflated valuations caused by thin evidence packs and
 * missed deadlines because we surface opportunities your consultant missed" is
 * a causal claim about the world that nobody wrote. A reader cannot see the
 * seam; a regulator or an expert reader can.
 *
 * The rule, deterministic: for each causal connector in a sentence, the clause
 * before it and the clause after it must both be stated by ONE evidence line,
 * or the sentence has joined facts the evidence does not join.
 *  - A strong connector ("caused by", "due to", "driven by", "results from",
 *    "as a result of") is flagged whenever no single line states both sides.
 *  - "because" is also how a mechanism is legitimately written ("X because Y"),
 *    so it is flagged only when BOTH sides are lifted from evidence and from
 *    different lines. A mechanism the writer composed from its own words is
 *    left to the claims judge.
 *
 * Pure. No I/O.
 */

import type { AdCopyClaimsField, AdCopyClaimsFinding } from './ad-copy-claims';

const STRONG = String.raw`caused\s+by|causing|due\s+to|driven\s+by|results?\s+from|resulting\s+from|as\s+a\s+result\s+of|stems?\s+from|stemming\s+from|owing\s+to`;
const WEAK = String.raw`because(?:\s+of)?|since|as\s+a\s+consequence\s+of`;
const CONNECTOR = new RegExp(`\\b(${STRONG}|${WEAK})\\b`, 'gi');
const STRONG_ONLY = new RegExp(`^(?:${STRONG})$`, 'i');

const FUNCTION_WORDS = new Set(
  'a an the of and or to in on for with by at from that this these those is are was were be been it its as their your our we you they them than but not no without who which what when where how also into over only more most such each any all can may will would could should has have had do does did us if so while'.split(
    ' ',
  ),
);

/** Same stem for "challenge/challenging/challenged", "valuation/valuations". */
function stem(word: string): string {
  return word.length > 5 ? word.slice(0, 5) : word.replace(/s$/, '');
}

function contentStems(text: string): string[] {
  const words = text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  return [
    ...new Set(
      words
        .filter((word) => word.length > 2 && !FUNCTION_WORDS.has(word))
        .map((word) => stem(word)),
    ),
  ];
}

const MIN_CONTENT = 2;
const COVERAGE = 0.7;

function covers(lineStems: ReadonlySet<string>, clause: readonly string[]): boolean {
  if (clause.length < MIN_CONTENT) return false;
  const hit = clause.filter((token) => lineStems.has(token)).length;
  return hit / clause.length >= COVERAGE;
}

function clausesWithConnectors(sentence: string): Array<{
  left: string;
  right: string;
  connector: string;
}> {
  const parts = sentence.split(CONNECTOR);
  const pairs: Array<{ left: string; right: string; connector: string }> = [];
  for (let index = 1; index < parts.length; index += 2) {
    pairs.push({
      left: parts[index - 1] ?? '',
      connector: parts[index] ?? '',
      right: parts[index + 1] ?? '',
    });
  }
  return pairs;
}

function splitSentences(text: string): string[] {
  return text.split(/(?<=[.!?])\s+(?=[A-Z0-9"'“(])/);
}

/** A clause ends at the next punctuation break, so "…caused by X, and we…" reads X alone. */
function trimClause(clause: string, side: 'left' | 'right'): string {
  const pieces = clause.split(/[;:—–()]|,\s/);
  return (side === 'left' ? pieces[pieces.length - 1] : pieces[0]) ?? clause;
}

export function findInventedCausation(
  fields: readonly AdCopyClaimsField[],
  sources: readonly string[],
): AdCopyClaimsFinding[] {
  const lines = sources.map((source) => new Set(contentStems(source)));
  const findings: AdCopyClaimsFinding[] = [];

  for (const { field, text } of fields) {
    if (!text) continue;
    for (const sentence of splitSentences(text)) {
      for (const pair of clausesWithConnectors(sentence)) {
        const left = contentStems(trimClause(pair.left, 'left'));
        const right = contentStems(trimClause(pair.right, 'right'));
        if (left.length < MIN_CONTENT || right.length < MIN_CONTENT) continue;
        const strong = STRONG_ONLY.test(pair.connector.replace(/\s+/g, ' '));
        const joined = lines.some((line) => covers(line, left) && covers(line, right));
        if (joined) continue;
        if (!strong) {
          const leftLifted = lines.some((line) => covers(line, left));
          const rightLifted = lines.some((line) => covers(line, right));
          if (!(leftLifted && rightLifted)) continue;
        }
        const connector = pair.connector.toLowerCase().replace(/\s+/g, ' ');
        findings.push({
          field,
          detail: `${field} joins two facts with "${connector}" that no line of the evidence joins ("${sentence.trim()}"). That is a cause the source never stated.`,
          fix: `In ${field}, state each fact as the source states it, as two separate statements, or use only a cause that one evidence line states in full. Do not connect two source facts with "${connector}".`,
          sentence: sentence.trim(),
        });
        break;
      }
    }
  }
  return findings;
}
